import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../hooks/useSocket';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import VirtualCallTable from '../components/VirtualCallTable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const API = `${BACKEND_URL}/api`;

const normalizeCampaignConfig = (campaign) => {
  if (!campaign) return campaign;
  return {
    ...campaign,
    customFields: campaign.customFields || campaign.custom_fields || [],
    callCategories: campaign.callCategories || campaign.call_categories || []
  };
};

const normalizeCategoryId = (value) => {
  if (!value) return '';
  const base = value.toString().split('-')[0] || value;
  return base.trim().toUpperCase();
};

const DEFAULT_CATEGORY_OPTIONS = [
  { id: 'CONFIRMED', name: 'Confirmed' },
  { id: 'CALL_BACK', name: 'Call Back' },
  { id: 'NOT_INTERESTED', name: 'Not Interested' }
];

const CampaignDetails = ({ campaign, onClose }) => {
  const { user } = useAuth();
  const { on, off } = useSocket();
  const [campaignDetails, setCampaignDetails] = useState(normalizeCampaignConfig(campaign));
  const [loading, setLoading] = useState(true);
  const [totalCallsCount, setTotalCallsCount] = useState(null);
  
  // Use infinite scroll for calls with memory limit
  const {
    data: callsData,
    loading: loadingCalls,
    hasMore: hasMoreCalls,
    observerTarget: callsObserverTarget,
    reset: resetCalls
  } = useInfiniteScroll(
    async (page, limit) => {
      if (!campaign?._id) return { data: { calls: [], pagination: { hasNextPage: false } } };
      const digits = (callSearchTerm || '').replace(/\D/g, '');
      const response = await axios.get(`${API}/calls`, {
        params: {
          campaignId: campaign._id, page, limit,
          // ≥4 digits → server-side phone search (format-independent); shorter/text
          // terms keep the existing client-side refinement over loaded pages.
          phone: digits.length >= 4 ? digits : undefined,
          category: callCategoryFilter || undefined,
          lead: callLeadOnly ? 'true' : undefined,
        }
      });
      // Store total count from first page
      if (page === 1 && response.data?.pagination?.total) {
        setTotalCallsCount(response.data.pagination.total);
      }
      return response;
    },
    {
      initialPage: 1,
      pageSize: 20,
      enabled: !!campaign?._id,
      maxItemsInMemory: 1000 // Keep only last 1000 items in memory
    }
  );

  const calls = callsData || [];
  const [activeTab, setActiveTab] = useState('overview');

  const {
    data: recordingsData,
    loading: loadingRecordings,
    hasMore: hasMoreRecordings,
    observerTarget: recordingsObserverTarget,
    reset: resetRecordings
  } = useInfiniteScroll(
    async (page, limit) => {
      if (!campaign?._id) return { data: { calls: [], pagination: { hasNextPage: false } } };
      const response = await axios.get(`${API}/calls`, {
        params: {
          campaignId: campaign._id,
          hasRecording: 'true',
          page,
          limit,
          ...(recordingSearchPhoneDebounced ? { phone: recordingSearchPhoneDebounced } : {})
        }
      });
      return response;
    },
    {
      initialPage: 1,
      pageSize: 50,
      enabled: !!campaign?._id && activeTab === 'recordings',
      maxItemsInMemory: 2000
    }
  );
  const recordingsCalls = recordingsData || [];

  const {
    data: contactsData,
    loading: loadingContacts,
    hasMore: hasMoreContacts,
    observerTarget: contactsObserverTarget,
    reset: resetContacts
  } = useInfiniteScroll(
    async (page, limit) => {
      if (!campaign?._id) return { data: { contacts: [], pagination: { hasNextPage: false } } };
      const response = await axios.get(`${API}/campaigns/${campaign._id}/contacts`, {
        params: { page, limit, search: contactSearchTerm || undefined }
      });
      return response;
    },
    {
      initialPage: 1,
      pageSize: 50,
      enabled: !!campaign?._id && activeTab === 'contacts',
      maxItemsInMemory: 2000
    }
  );
  const contacts = contactsData || [];

  const [contactSearchTerm, setContactSearchTerm] = useState('');
  const [callSearchTerm, setCallSearchTerm] = useState('');
  const [callCategoryFilter, setCallCategoryFilter] = useState('');
  const [callLeadOnly, setCallLeadOnly] = useState(false);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [filteredCalls, setFilteredCalls] = useState([]);
  const [recordingSearchPhone, setRecordingSearchPhone] = useState('');
  const [recordingSearchPhoneDebounced, setRecordingSearchPhoneDebounced] = useState('');
  const recordingSearchFirstMount = useRef(true);

  useEffect(() => {
    const t = setTimeout(() => setRecordingSearchPhoneDebounced(recordingSearchPhone), 400);
    return () => clearTimeout(t);
  }, [recordingSearchPhone]);

  useEffect(() => {
    if (recordingSearchFirstMount.current) {
      recordingSearchFirstMount.current = false;
      return;
    }
    resetRecordings();
  }, [recordingSearchPhoneDebounced]);

  // Audio playback state
  const [audioPlayer, setAudioPlayer] = useState(null);
  const [currentPlayingRecording, setCurrentPlayingRecording] = useState(null);

  const getCallCategoryValue = (call) => (call?.category || call?.enrichment?.category || '').trim();
  const getLeadQualityValue = (call) => (call?.lead_quality || call?.leadQuality || call?.enrichment?.lead_quality || '').toUpperCase();

  const findCategoryMeta = (value) => {
    if (!value) return null;
    const normalizedValue = normalizeCategoryId(value);
    return (campaignDetails?.callCategories || []).find(cat =>
      normalizeCategoryId(cat.id || cat.name) === normalizedValue
    );
  };

  const renderCategoryBadge = (value) => {
    if (!value) {
      return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">N/A</span>;
    }
    const meta = findCategoryMeta(value);
    const label = meta?.name || value;
    if (meta?.color) {
      return (
        <span
          className="px-2 py-1 text-xs rounded-full border"
          style={{ borderColor: meta.color, color: meta.color }}
        >
          {label}
        </span>
      );
    }
    return (
      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
        {label}
      </span>
    );
  };

  const renderLeadQualityBadge = (value, endReason, leadReason) => {
    if (!value) {
      return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">N/A</span>;
    }
    const upperValue = value.toUpperCase();
    const isLead = upperValue === 'LEAD' || upperValue === 'FORTE' || upperValue === 'MOYENNE';
    const className = isLead
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800';
    const label = isLead ? '✅ Lead' : '❌ Not Lead';
    return (
      <div className="flex flex-col gap-0.5">
        <span className={`px-2 py-1 text-xs rounded-full ${className}`} title={leadReason ? `Reason: ${leadReason}` : undefined}>
          {label}
        </span>
        {leadReason && (
          <span className="text-xs text-gray-400 max-w-[130px] truncate" title={leadReason}>
            {leadReason}
          </span>
        )}
        {endReason && (
          <span className="text-xs text-gray-500 max-w-[130px] truncate" title={endReason}>
            {endReason}
          </span>
        )}
      </div>
    );
  };

  const renderEnrichmentFields = (call) => {
    const fields = campaignDetails?.customFields || [];
    const ownerHousingFields = [
      { name: 'proprietaire_ou_locataire', label: 'Propriétaire ou Locataire' },
      { name: 'maison_ou_appartement', label: 'Maison ou Appartement' }
    ];
    const fieldsWithValues = (fields.length ? fields : []).filter(field => {
      const value = call?.enrichment?.[field.name];
      return value !== undefined && value !== null && value !== '';
    });
    const ownerHousingWithValues = ownerHousingFields.filter(f => {
      const v = call?.enrichment?.[f.name];
      return v !== undefined && v !== null && v !== '';
    });
    const hasAny = fieldsWithValues.length > 0 || ownerHousingWithValues.length > 0;
    if (!hasAny) {
      return <span className="text-gray-400 text-xs block">No data</span>;
    }
    return (
      <div className="space-y-2">
        {ownerHousingWithValues.map((field) => (
          <div
            key={field.name}
            className="block w-full pb-2 border-b border-gray-200"
            style={{ minHeight: 'fit-content' }}
          >
            <div className="font-semibold text-gray-700 text-[10px] uppercase tracking-wide mb-1 leading-tight block">
              {field.label}
            </div>
            <div className="text-gray-800 break-words leading-snug text-[11px] block w-full">
              {String(call?.enrichment?.[field.name] ?? '')}
            </div>
          </div>
        ))}
        {fieldsWithValues.map((field, index) => {
          const value = call?.enrichment?.[field.name];
          return (
            <div
              key={field.name || field.label || index}
              className="block w-full pb-2 border-b border-gray-200 last:border-b-0 last:pb-0"
              style={{ minHeight: 'fit-content' }}
            >
              <div className="font-semibold text-gray-700 text-[10px] uppercase tracking-wide mb-1 leading-tight block">
                {field.label || field.name}
              </div>
              <div className="text-gray-800 break-words leading-snug text-[11px] block w-full">
                {value.toString()}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  useEffect(() => {
    if (user) {
      loadCampaignDetails();
    }
  }, [campaign._id, user]);

  // Live updates. A running campaign emits call:created / call:updated many times a
  // second (one per status change, per call). Resetting the list on each event wiped
  // the table, lost the scroll position and made the search field unusable.
  // Now: events are coalesced to one refresh per LIVE_REFRESH_MIN_MS, and while the
  // reader is searching/filtering or has scrolled down, the refresh is held behind
  // an "Actualiser" button instead of pulling the list out from under them.
  const LIVE_REFRESH_MIN_MS = 15000;
  const [heldCallUpdates, setHeldCallUpdates] = useState(0);
  const liveRefreshTimer = useRef(null);
  const liveRefreshLastAt = useRef(0);
  const liveViewRef = useRef({ tab: 'overview', filtering: false, recordingSearch: '' });
  useEffect(() => {
    liveViewRef.current = {
      tab: activeTab,
      filtering: !!(callSearchTerm || callCategoryFilter || callLeadOnly),
      recordingSearch: recordingSearchPhoneDebounced
    };
  }, [activeTab, callSearchTerm, callCategoryFilter, callLeadOnly, recordingSearchPhoneDebounced]);

  const applyLiveRefresh = useCallback(() => {
    liveRefreshLastAt.current = Date.now();
    setHeldCallUpdates(0);
    resetCalls();
    if (liveViewRef.current.tab === 'recordings' && !liveViewRef.current.recordingSearch) resetRecordings();
  }, [resetCalls, resetRecordings]);

  const scheduleLiveRefresh = useCallback(() => {
    const v = liveViewRef.current;
    const readerIsBusy = v.tab === 'calls' && (v.filtering || window.scrollY > 300);
    if (readerIsBusy) {
      setHeldCallUpdates(n => n + 1);
      return;
    }
    if (liveRefreshTimer.current) return;   // a refresh is already scheduled
    const wait = Math.max(0, LIVE_REFRESH_MIN_MS - (Date.now() - liveRefreshLastAt.current));
    liveRefreshTimer.current = setTimeout(() => {
      liveRefreshTimer.current = null;
      applyLiveRefresh();
    }, wait);
  }, [applyLiveRefresh]);

  useEffect(() => () => { if (liveRefreshTimer.current) clearTimeout(liveRefreshTimer.current); }, []);

  // Set up real-time socket listeners (merge payload into campaign details)
  useEffect(() => {
    const handleCampaignUpdate = (data) => {
      if (data.campaignId !== campaign._id.toString()) return;

      setCampaignDetails(prev => ({
        ...prev,
        status: data.status ?? prev.status,
        execution_state: data.execution_state ?? prev.execution_state,
        calls_made: data.calls_made !== undefined ? data.calls_made : prev.calls_made,
        confirmed_calls: data.confirmed_calls !== undefined ? data.confirmed_calls : prev.confirmed_calls,
        success_rate: data.success_rate !== undefined ? data.success_rate : prev.success_rate,
        analytics_snapshot: data.analytics_snapshot ?? prev.analytics_snapshot,
        recordings_count: data.recordings_count !== undefined ? data.recordings_count : prev.recordings_count
      }));

      if (data.calls_made !== undefined) setTotalCallsCount(data.calls_made);
    };

    const handleCallCreated = (data) => {
      if (data.campaignId === campaign._id.toString()) {
        setCampaignDetails(prev => ({
          ...prev,
          calls_made: (prev.calls_made || 0) + 1
        }));
        scheduleLiveRefresh();
      }
    };

    const handleCallUpdated = (data) => {
      if (data.campaignId === campaign._id.toString()) scheduleLiveRefresh();
    };

    const handleContactUpdate = (data) => {
      if (data.campaignId === campaign._id.toString()) resetContacts();
    };

    on('campaign:updated', handleCampaignUpdate);
    on('call:created', handleCallCreated);
    on('call:updated', handleCallUpdated);
    on('campaign:contact:updated', handleContactUpdate);

    return () => {
      off('campaign:updated', handleCampaignUpdate);
      off('call:created', handleCallCreated);
      off('call:updated', handleCallUpdated);
      off('campaign:contact:updated', handleContactUpdate);
    };
  }, [campaign._id, on, off, scheduleLiveRefresh, resetContacts]);

  // Reset contacts when search term changes (server-side filtering)
  useEffect(() => {
    if (campaign?._id && activeTab === 'contacts') {
      resetContacts();
    }
  }, [contactSearchTerm, campaign?._id, activeTab, resetContacts]);

  // Refetch calls from the server whenever a call filter changes.
  useEffect(() => {
    if (!campaign?._id) return;
    const t = setTimeout(() => { setHeldCallUpdates(0); resetCalls(); }, 400);   // debounce typing
    return () => clearTimeout(t);
  }, [callSearchTerm, callCategoryFilter, callLeadOnly]);

  // Download the CURRENT filter selection as CSV — all matching rows, not just
  // the pages loaded by the infinite scroll.
  const downloadFilteredCsv = async () => {
    try {
      const digits = (callSearchTerm || '').replace(/\D/g, '');
      const r = await axios.get(`${API}/calls`, {
        params: {
          campaignId: campaign._id, page: 1, limit: 5000,
          phone: digits.length >= 4 ? digits : undefined,
          category: callCategoryFilter || undefined,
          lead: callLeadOnly ? 'true' : undefined,
        }
      });
      const rows = r.data?.calls || r.data?.data?.calls || [];
      const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const header = ['Téléphone', 'Statut', 'Catégorie', 'Lead', 'Durée (s)', 'Date', 'Résumé'];
      const csv = [header.join(';')].concat(rows.map(c => [
        c.phone, c.status, (c.category || c.enrichment?.category || ''),
        (c.lead_quality || ''), (c.duration ?? ''),
        c.createdAt ? new Date(c.createdAt).toLocaleString('fr-FR') : '',
        (c.summary || '').slice(0, 200),
      ].map(esc).join(';'))).join('\n');
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${campaign.name || 'campagne'}_appels_filtres.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      console.error('CSV download failed', e);
    }
  };

  // Set filteredContacts to contacts (server-side filtering handles search)
  useEffect(() => {
    setFilteredContacts(contacts);
  }, [contacts]);

  // Effet pour filtrer les appels
  useEffect(() => {
    console.log('🔍 Filtrage appels - calls.length:', calls.length, 'callSearchTerm:', callSearchTerm);

    if (!callSearchTerm.trim()) {
      setFilteredCalls(calls);
      console.log('✅ FilteredCalls mis à jour avec', calls.length, 'appels');
    } else {
      const digits = callSearchTerm.replace(/\D/g, '');
      if (digits.length >= 4) {
        // The server already matched on digits (format-independent); re-testing
        // "07 69" against "+33769…" here used to throw those matches away.
        setFilteredCalls(calls);
        return;
      }
      const filtered = calls.filter(call => {
        const searchLower = callSearchTerm.toLowerCase();
        const leadQualityValue = getLeadQualityValue(call);
        return (
          (digits && call.phone && call.phone.replace(/\D/g, '').includes(digits)) ||
          (call.phone && call.phone.includes(searchLower)) ||
          (call.status && call.status.toLowerCase().includes(searchLower)) ||
          ((call.category || call.enrichment?.category) &&
            (call.category || call.enrichment?.category).toLowerCase().includes(searchLower)) ||
          (leadQualityValue && leadQualityValue.toLowerCase().includes(searchLower)) ||
          (call.enrichment && JSON.stringify(call.enrichment).toLowerCase().includes(searchLower)) ||
          (call.transcript && JSON.stringify(call.transcript).toLowerCase().includes(searchLower))
        );
      });
      setFilteredCalls(filtered);
      console.log('🔍 FilteredCalls filtré:', filtered.length, 'appels');
    }
  }, [calls, callSearchTerm]);

  const loadCampaignDetails = async () => {
    try {
      setLoading(true);

      const campaignRes = await axios.get(`${API}/campaigns/${campaign._id}`);
      const foundCampaign = campaignRes.data;
      if (!foundCampaign) {
        throw new Error('Campaign not found');
      }

      setCampaignDetails(normalizeCampaignConfig(foundCampaign));
      setTotalCallsCount(null); // Reset to reload from API

      resetCalls();
      resetContacts();
    } catch (error) {
      console.error('Error loading campaign details:', error);
      toast.error('Erreur lors du chargement des détails');
    } finally {
      setLoading(false);
    }
  };

  const exportCampaign = async (format = 'xlsx') => {
    try {
      const response = await axios.get(`${API}/campaigns/${campaign._id}/export`, {
        params: { format },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `campagne_${campaign.name}.${format}`);
      
      // Check if link is already in DOM before appending
      if (!document.body.contains(link)) {
        document.body.appendChild(link);
      }
      
      link.click();
      
      // Clean up with error handling
      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
        window.URL.revokeObjectURL(url);
      }, 100);

      toast.success(`Export ${format.toUpperCase()} téléchargé !`);
    } catch (error) {
      console.error('Error exporting campaign:', error);
      toast.error('Erreur lors de l\'export');
    }
  };

  const getRecordingUrl = (call, forDownload = false) => {
    if (!call.recording) return null;
    
    // Extract filename from recording path: "recordings/call-1765937700.53974.wav" -> "call-1765937700.53974.wav"
    const filename = call.recording.substring(call.recording.lastIndexOf('/') + 1);
    const baseUrl = `${BACKEND_URL}/api/recordings/${filename}`;
    
    return forDownload ? `${baseUrl}?download=true` : baseUrl;
  };

  const playRecording = (call) => {
    console.log("call:", call)
    const recordingUrl = getRecordingUrl(call, false);
    if (!recordingUrl) {
      toast.error('Recording URL is not available');
      return;
    }

    const callId = call._id || call.callSid;
    console.log('Playing recording:', { callId, url: recordingUrl });
    
    // Stop current playback
    if (audioPlayer) {
      // Remove all event listeners to prevent error handlers from firing
      audioPlayer.onerror = null;
      audioPlayer.oncanplaythrough = null;
      audioPlayer.onended = null;
      
      audioPlayer.pause();
      audioPlayer.src = '';
      setAudioPlayer(null);
    }

    const audio = new Audio();
    audio.preload = 'auto';
    audio.crossOrigin = 'anonymous';

    // Better error handling (same as ManualCallsPage)
    audio.onerror = () => {
      // Only show error if this audio element is still the current player
      // Check by comparing callId to prevent showing error when user stops playback
      if (currentPlayingRecording === callId) {
        console.error('Audio playback error:', audio.error);
        let msg = 'Failed to play recording.';
        if (audio.error?.code === 4) {
          msg = 'Audio format not supported. Try downloading the file.';
        }
        toast.error(msg);
        setCurrentPlayingRecording(null);
        setAudioPlayer(null);
      } else {
        // Error occurred but user already stopped or switched to another recording
        console.log('Audio error ignored - playback was stopped or switched');
      }
    };

    audio.oncanplaythrough = () => {
      audio.play().catch(e => {
        console.warn('Play interrupted (user interaction needed?):', e);
        toast.warn('Click Play again to start playback');
      });
    };

    audio.onended = () => {
      setCurrentPlayingRecording(null);
      setAudioPlayer(null);
    };

    audio.src = recordingUrl;
    setAudioPlayer(audio);
    setCurrentPlayingRecording(callId);
    
    // Attempt to play
    audio.load();
  };

  const stopRecording = () => {
    if (audioPlayer) {
      // Remove all event listeners to prevent error handlers from firing
      audioPlayer.onerror = null;
      audioPlayer.oncanplaythrough = null;
      audioPlayer.onended = null;
      
      audioPlayer.pause();
      audioPlayer.src = '';
      setAudioPlayer(null);
      setCurrentPlayingRecording(null);
    }
  };

  const downloadCallRecording = async (callId) => {
    try {
      const call = calls.find(c => c._id === callId) || recordingsCalls.find(c => c._id === callId);
      if (!call) {
        toast.error('Call not found');
        return;
      }

      const downloadUrl = getRecordingUrl(call, true); // true = for download
      if (!downloadUrl) {
        toast.error('Recording URL is not available');
        return;
      }

      // Get filename from call
      let filename = 'recording.wav';
      if (call.recording) {
        const url = call.recording.trim();
        if (url.includes('/')) {
          filename = url.substring(url.lastIndexOf('/') + 1);
        } else {
          filename = url;
        }
      } else if (call.callSid) {
        filename = `call-${call.callSid}.wav`;
      }

      // Fetch and download
      toast.info('Preparing download...');
      const response = await fetch(downloadUrl);
      
      if (!response.ok) {
        throw new Error('Failed to fetch recording');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      toast.success(`Download started: ${filename}`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download recording');
    }
  };


  const downloadCallTranscript = async (callId) => {
    try {
      console.log('📄 Téléchargement transcription pour:', callId);
      const response = await axios.get(`${API}/calls/${callId}/transcript`, {
        responseType: 'blob'
      });

      // Check if response is actually a blob (success) or JSON error
      if (response.data instanceof Blob) {
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `transcription_${callId}.txt`);
        
        if (!document.body.contains(link)) {
          document.body.appendChild(link);
        }
        
        link.click();
        
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
          window.URL.revokeObjectURL(url);
        }, 100);

        toast.success('Transcription téléchargée !');
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error downloading transcript:', error);
      let errorMessage = 'Erreur lors du téléchargement de la transcription';
      
      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = error.response.data?.error || 'Transcription non disponible pour cet appel';
        } else if (error.response.data && typeof error.response.data === 'object') {
          errorMessage = error.response.data.error || error.response.data.message || errorMessage;
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      'draft': 'bg-gray-100 text-gray-800',
      'active': 'bg-green-100 text-green-800',
      'paused': 'bg-yellow-100 text-yellow-800',
      'completed': 'bg-blue-100 text-blue-800',
      'failed': 'bg-red-100 text-red-800'
    };

    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';

    try {
      // 🔧 CORRECTION TIMEZONE: Forcer l'affichage en heure française
      const date = new Date(dateString);

      return date.toLocaleString('fr-FR', {
        timeZone: 'Europe/Paris',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      console.error('Erreur formatage date:', error);
      return dateString;
    }
  };

  // Calculate campaign statistics
  const getCampaignStatistics = () => {
    const contactsCount = campaignDetails.contacts_count || contacts.length || 0;
    const callsMade = Math.min(campaignDetails.calls_made || calls.length || 0, contactsCount || Infinity);
    const remainingCalls = Math.max(0, contactsCount - callsMade);
    
    // Calculate completion percentage (capped at 100%)
    let completionPercentage = 0;
    if (contactsCount > 0) {
      completionPercentage = Math.min((callsMade / contactsCount) * 100, 100);
    }
    
    // Calculate phone minutes consumed (sum of all call durations in seconds, divided by 60)
    const totalSeconds = calls.reduce((sum, call) => sum + (call.duration || 0), 0);
    const phoneMinutes = totalSeconds / 60;
    
    // Get validated leads (confirmed_calls from campaign)
    const validatedLeads = campaignDetails.confirmed_calls || 0;
    
    return {
      callsMade,
      remainingCalls,
      completionPercentage,
      phoneMinutes,
      validatedLeads
    };
  };

  const stats = getCampaignStatistics();

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8">
          <div className="animate-pulse">Chargement des détails...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-7xl max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold">{campaignDetails.name}</h2>
              <p className="text-blue-100 mt-1">{campaignDetails.description}</p>
              <div className="flex items-center space-x-4 mt-3">
                {getStatusBadge(campaignDetails.status)}
                <span className="text-blue-100">
                  📞 {campaignDetails.contacts_count || contacts.length || 0} contacts • 🎯 {totalCallsCount !== null ? totalCallsCount : (campaignDetails.calls_made || calls.length || 0)} appels
                </span>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => exportCampaign('xlsx')}
                className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition-colors"
              >
                📊 Export XLSX
              </button>
              <button
                onClick={() => exportCampaign('csv')}
                className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition-colors"
              >
                📄 Export CSV
              </button>
              <button
                onClick={onClose}
                className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition-colors"
              >
                ✕
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { id: 'overview', name: '📊 Vue d\'ensemble' },
              { id: 'contacts', name: '👥 Contacts' },
              { id: 'calls', name: '📞 Appels' },
              { id: 'recordings', name: '🎵 Enregistrements' },
              { id: 'analytics', name: '📈 Analytics' },
              { id: 'config', name: '⚙️ Configuration' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[70vh] p-6">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Statistiques */}
              <div className="lg:col-span-2 space-y-6">
                {/* Métriques principales */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-blue-600">{contacts.length}</div>
                    <div className="text-sm text-blue-800">Contacts Total</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-green-600">
                      {calls.filter(call => call.status === 'completed').length}
                    </div>
                    <div className="text-sm text-green-800">Appels Réussis</div>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-yellow-600">
                      {calls.filter(call => call.status === 'ringing').length}
                    </div>
                    <div className="text-sm text-yellow-800">En Cours</div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-red-600">
                      {calls.filter(call => call.status === 'failed').length}
                    </div>
                    <div className="text-sm text-red-800">Échecs</div>
                  </div>
                </div>

                {/* Script de la campagne */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3">📝 Script de la Campagne</h3>
                  <div className="bg-white border rounded p-4 font-mono text-sm whitespace-pre-wrap">
                    {campaignDetails.script}
                  </div>
                </div>

                {/* Configuration AMD */}
                {campaignDetails.amd_config?.enabled && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold mb-3">🎯 Détection de Répondeur (AMD)</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Silence initial:</span>
                        <div>{campaignDetails.amd_config.initial_silence}ms</div>
                      </div>
                      <div>
                        <span className="font-medium">Message d'accueil:</span>
                        <div>{campaignDetails.amd_config.greeting}ms</div>
                      </div>
                      <div>
                        <span className="font-medium">Temps d'analyse:</span>
                        <div>{campaignDetails.amd_config.total_analysis_time}ms</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Informations latérales */}
              <div className="space-y-6">
                <div className="bg-white border rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3">ℹ️ Informations</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Créée le:</span>
                      <div>{formatDate(campaignDetails.createdAt || campaignDetails.created_at)}</div>
                    </div>
                    <div>
                      <span className="font-medium">Mise à jour:</span>
                      <div>{formatDate(campaignDetails.updatedAt || campaignDetails.updated_at)}</div>
                    </div>
                    <div>
                      <span className="font-medium">Statut:</span>
                      <div>{getStatusBadge(campaignDetails.status)}</div>
                    </div>
                  </div>
                </div>

                <div className="bg-white border rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3">🤖 Configuration IA</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">STT:</span>
                      <div>{campaignDetails.ai_config?.stt_model || 'whisper_tiny'}</div>
                    </div>
                    <div>
                      <span className="font-medium">LLM:</span>
                      <div>{campaignDetails.ai_config?.llm_model || 'gpt2_small'}</div>
                    </div>
                    <div>
                      <span className="font-medium">TTS:</span>
                      <div>{campaignDetails.ai_config?.tts_model || 'espeak_french'}</div>
                    </div>
                  </div>
                </div>

                <div className="bg-white border rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3">📱 Configuration SIP</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Fournisseur:</span>
                      <div>{campaignDetails.sip_config?.provider || 'flashcall'}</div>
                    </div>
                    <div>
                      <span className="font-medium">Caller ID:</span>
                      <div>{campaignDetails.sip_config?.caller_id || 'N/A'}</div>
                    </div>
                    <div>
                      <span className="font-medium">Appels simultanés:</span>
                      <div>{campaignDetails.concurrent_calls || 5}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'contacts' && (() => {
            // Generate unified column order based on all contacts
            const getUnifiedColumnOrder = () => {
              if (contacts.length === 0) return [];

              // Get all unique keys from all contacts
              const allKeys = new Set();
              contacts.forEach(contact => {
                Object.keys(contact).forEach(key => {
                  if (!['id', 'campaign_id', '_id', 'created_at', 'updated_at', 'status', 'metadata'].includes(key)) {
                    allKeys.add(key);
                  }
                });
              });

              // Define preferred order (handling both French and English field names)
              const preferredOrder = [
                'nom', 'name',           // Name fields
                'prenom', 'firstName', 'first_name',  // First name fields
                'phone', 'telephone',   // Phone fields
                'email', 'mail', 'e_mail',  // Email fields
                'address', 'adresse', 'addresse',  // Address fields
                'city', 'ville', 'ville_name',  // City fields
                'postal_code', 'code_postal', 'postal', 'code postal'  // Postal code fields
              ];

              const orderedColumns = [];
              const remainingKeys = [];

              // Add preferred columns in order (only if they exist)
              preferredOrder.forEach(prefKey => {
                if (allKeys.has(prefKey)) {
                  orderedColumns.push(prefKey);
                  allKeys.delete(prefKey);
                }
              });

              // Add remaining keys
              allKeys.forEach(key => {
                remainingKeys.push(key);
              });

              // Sort remaining keys alphabetically
              remainingKeys.sort();

              return [...orderedColumns, ...remainingKeys];
            };

            const unifiedColumns = getUnifiedColumnOrder();

            // Column labels mapping
            const columnLabels = {
              'nom': 'Nom',
              'name': 'Nom',
              'prenom': 'Prénom',
              'firstName': 'Prénom',
              'first_name': 'Prénom',
              'phone': 'Téléphone',
              'telephone': 'Téléphone',
              'email': 'Email',
              'mail': 'Email',
              'e_mail': 'Email',
              'address': 'Adresse',
              'adresse': 'Adresse',
              'addresse': 'Adresse',
              'city': 'Ville',
              'ville': 'Ville',
              'ville_name': 'Ville',
              'postal_code': 'Code Postal',
              'code_postal': 'Code Postal',
              'postal': 'Code Postal',
              'code postal': 'Code Postal'
            };

            // Function to get contact value for a column (handles multiple field name variations)
            const getContactValue = (contact, column) => {
              // Direct match
              if (contact[column] !== undefined && contact[column] !== null && contact[column] !== '') {
                return contact[column];
              }

              // Try lowercase
              const lowerColumn = column.toLowerCase();
              if (contact[lowerColumn] !== undefined && contact[lowerColumn] !== null && contact[lowerColumn] !== '') {
                return contact[lowerColumn];
              }

              // Try field name variations
              const variations = {
                'nom': ['name', 'firstName', 'first_name'],
                'name': ['nom', 'firstName', 'first_name'],
                'prenom': ['firstName', 'first_name', 'name'],
                'firstName': ['prenom', 'first_name', 'nom'],
                'phone': ['telephone'],
                'telephone': ['phone'],
                'email': ['mail', 'e_mail'],
                'mail': ['email', 'e_mail'],
                'address': ['adresse', 'addresse'],
                'adresse': ['address', 'addresse'],
                'city': ['ville', 'ville_name'],
                'ville': ['city', 'ville_name'],
                'postal_code': ['code_postal', 'postal', 'code postal'],
                'code_postal': ['postal_code', 'postal', 'code postal']
              };

              if (variations[column]) {
                for (const variant of variations[column]) {
                  if (contact[variant] !== undefined && contact[variant] !== null && contact[variant] !== '') {
                    return contact[variant];
                  }
                }
              }

              return '-';
            };

            return (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">👥 Contacts Importés ({filteredContacts.length}/{contacts.length})</h3>
                  <div className="space-x-2">
                    <button
                      onClick={() => exportCampaign('xlsx')}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                    >
                      📊 Export XLSX
                    </button>
                  </div>
                </div>

                {/* Champ de recherche pour contacts */}
                <div className="mb-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Rechercher par nom, prénom, téléphone, email ou ville..."
                      value={contactSearchTerm}
                      onChange={(e) => setContactSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    {contactSearchTerm && (
                      <button
                        onClick={() => setContactSearchTerm('')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        {unifiedColumns.map(column => (
                          <th key={column} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            {columnLabels[column] || column.charAt(0).toUpperCase() + column.slice(1)}
                          </th>
                        ))}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut Appel</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredContacts.map((contact, index) => {
                        const contactPhone = contact.phone || contact.telephone;
                        const contactCall = calls.find(call => (call.phone || call.receiver) === contactPhone);

                        return (
                          <tr key={contact._id || contact.id || index}>
                            {unifiedColumns.map(column => (
                              <td key={column} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {getContactValue(contact, column)}
                              </td>
                            ))}
                            <td className="px-6 py-4 whitespace-nowrap">
                              {contactCall ? getStatusBadge(contactCall.status) : <span className="text-gray-500">Non appelé</span>}
                            </td>
                          </tr>
                        );
                      })}
                      {filteredContacts.length === 0 && !loadingContacts && (
                        <tr>
                          <td colSpan={unifiedColumns.length + 1} className="px-6 py-4 text-center text-sm text-gray-500">
                            Aucun contact trouvé
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {loadingContacts && (
                  <div className="py-4 text-center text-sm text-gray-500">Chargement...</div>
                )}
                {hasMoreContacts && <div ref={contactsObserverTarget} className="h-4" />}
              </div>
            );
          })()}

          {activeTab === 'calls' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">
                📞 Historique des Appels ({filteredCalls.length} affiché{filteredCalls.length > 1 ? 's' : ''}
                {totalCallsCount !== null ? ` · ${totalCallsCount} au total` : ''})
              </h3>

              {heldCallUpdates > 0 && (
                <div className="mb-3 flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                  <span>🔄 {heldCallUpdates} mise{heldCallUpdates > 1 ? 's' : ''} à jour en attente — la liste est figée pendant votre recherche.</span>
                  <button
                    onClick={applyLiveRefresh}
                    className="ml-3 px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Actualiser
                  </button>
                </div>
              )}

              {/* DEBUG INFO DÉTAILLÉ */}
              {/* Champ de recherche pour appels */}
              <div className="mb-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Rechercher par téléphone, statut, catégorie ou transcription..."
                    value={callSearchTerm}
                    onChange={(e) => setCallSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  {callSearchTerm && (
                    <button
                      onClick={() => setCallSearchTerm('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Filters: category + leads-only + CSV of the whole filtered set.
                  These query the SERVER, so they cover every call of the campaign —
                  not just the pages the infinite scroll has loaded. */}
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <select
                  value={callCategoryFilter}
                  onChange={(e) => setCallCategoryFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">Toutes les catégories</option>
                  {(campaignDetails.callCategories || []).map(cat => (
                    <option key={cat.id || cat.name} value={cat.id || cat.name}>{cat.name}</option>
                  ))}
                </select>

                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={callLeadOnly}
                    onChange={(e) => setCallLeadOnly(e.target.checked)}
                  />
                  Leads uniquement
                </label>

                {(callCategoryFilter || callLeadOnly || callSearchTerm) && (
                  <button
                    onClick={() => { setCallCategoryFilter(''); setCallLeadOnly(false); setCallSearchTerm(''); }}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    ✕ Réinitialiser
                  </button>
                )}

                <button
                  onClick={downloadFilteredCsv}
                  className="ml-auto px-3 py-2 text-sm bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg"
                  title="Télécharger tous les appels correspondant aux filtres (CSV)"
                >
                  ⬇️ Télécharger CSV
                </button>
              </div>

              <div className="overflow-x-auto">
                <VirtualCallTable
                  calls={filteredCalls}
                  contacts={contacts}
                  getStatusBadge={getStatusBadge}
                  getCallCategoryValue={getCallCategoryValue}
                  getLeadQualityValue={getLeadQualityValue}
                  renderCategoryBadge={renderCategoryBadge}
                  renderLeadQualityBadge={(value, call) => renderLeadQualityBadge(value, call?.end_reason, call?.lead_reason || call?.enrichment?.lead_reason)}
                  renderEnrichmentFields={renderEnrichmentFields}
                  formatDuration={formatDuration}
                  formatDate={formatDate}
                  downloadCallRecording={downloadCallRecording}
                  downloadCallTranscript={downloadCallTranscript}
                  height={500}
                  hasMore={hasMoreCalls}
                  loading={loadingCalls}
                  observerTarget={callsObserverTarget}
                />
              </div>
            </div>
          )}

          {activeTab === 'recordings' && (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <h3 className="text-lg font-semibold">🎵 Enregistrements {recordingsCalls.length > 0 && `(${recordingsCalls.length})`}</h3>
                <input
                  type="text"
                  placeholder="Rechercher par numéro..."
                  value={recordingSearchPhone}
                  onChange={(e) => setRecordingSearchPhone(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm w-56 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Téléphone</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qualité Lead</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catégorie</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durée</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {recordingsCalls.map((call) => {
                      const contact = contacts.find(c => (c.phone || c.telephone) === (call.phone || call.receiver));
                      const hasRecording = call.recording_status === 'completed' || call.recording;
                      return (
                        <tr key={call._id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {contact ? `${contact.nom || contact.name || ''} ${contact.prenom || contact.firstName || ''}`.trim() : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {call.phone || call.receiver}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(call.createdAt || call.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${call.recording_status === 'completed' ? 'bg-green-100 text-green-800' :
                                call.recording_status === 'recording' ? 'bg-yellow-100 text-yellow-800' :
                                  call.recording_status === 'failed' ? 'bg-red-100 text-red-800' :
                                    hasRecording ? 'bg-blue-100 text-blue-800' :
                                      'bg-gray-100 text-gray-800'
                              }`}>
                              {call.recording_status === 'completed' ? 'Disponible' :
                                call.recording_status === 'recording' ? 'En cours' :
                                  call.recording_status === 'failed' ? 'Échec' :
                                    hasRecording ? 'Disponible' : 'N/A'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {renderLeadQualityBadge(getLeadQualityValue(call), call.end_reason, call.lead_reason || call.enrichment?.lead_reason)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {renderCategoryBadge(getCallCategoryValue(call))}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {call.duration ? `${Math.floor(call.duration / 60)}:${String(call.duration % 60).padStart(2, '0')}` : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {hasRecording && (() => {
                              const callId = call._id || call.callSid;
                              const isPlaying = currentPlayingRecording === callId;
                              return (
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => {
                                      if (isPlaying) {
                                        stopRecording();
                                      } else {
                                        playRecording(call);
                                      }
                                    }}
                                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                      isPlaying
                                        ? 'bg-red-500 text-white hover:bg-red-600'
                                        : 'bg-blue-500 text-white hover:bg-blue-600'
                                    }`}
                                  >
                                    {isPlaying ? '⏹️ Stop' : '▶️ Play'}
                                  </button>
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    })}
                    {recordingsCalls.length === 0 && !loadingRecordings && (
                      <tr>
                        <td colSpan="8" className="px-6 py-4 text-center text-sm text-gray-500">
                          Aucun enregistrement disponible
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {loadingRecordings && (
                <div className="py-4 text-center text-sm text-gray-500">Chargement...</div>
              )}
              {hasMoreRecordings && <div ref={recordingsObserverTarget} className="h-4" />}
            </div>
          )}

          {activeTab === 'analytics' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">📈 Analytics</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-blue-600">{calls.length}</div>
                  <div className="text-sm text-blue-800">Total Appels</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-green-600">
                    {calls.filter(call => call.status === 'completed').length}
                  </div>
                  <div className="text-sm text-green-800">Appels Réussis</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-yellow-600">
                    {calls.filter(call => call.status === 'ringing' || call.status === 'answered').length}
                  </div>
                  <div className="text-sm text-yellow-800">En Cours</div>
                </div>
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-red-600">
                    {calls.filter(call => call.status === 'failed').length}
                  </div>
                  <div className="text-sm text-red-800">Échecs</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">Répartition par Catégorie</h4>
                  <div className="space-y-2">
                    {(campaignDetails.callCategories?.length ? campaignDetails.callCategories : DEFAULT_CATEGORY_OPTIONS).map(category => {
                      const categoryId = normalizeCategoryId(category.id || category.name);
                      const count = calls.filter(c => normalizeCategoryId(getCallCategoryValue(c)) === categoryId).length;
                      return (
                        <div key={categoryId || category.name} className="flex justify-between items-center">
                          <span className="text-sm text-gray-700">{category.name || category.id}</span>
                          <span className="text-sm font-medium">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-white border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">Qualité des Leads</h4>
                  <div className="space-y-2">
                    {[{ key: 'LEAD', label: '✅ Leads', match: v => v === 'LEAD' || v === 'FORTE' || v === 'MOYENNE' },
                      { key: 'NOT_LEAD', label: '❌ Not Lead', match: v => v === 'NOT_LEAD' || v === 'FAIBLE' || v === 'NONE' || v === '' }
                    ].map(({ key, label, match }) => {
                      const count = calls.filter(c => match(getLeadQualityValue(c))).length;
                      return (
                        <div key={key} className="flex justify-between items-center">
                          <span className="text-sm text-gray-700">{label}</span>
                          <span className="text-sm font-medium">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'config' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">⚙️ Configuration</h3>
              <div className="space-y-6">
                {/* Campaign Statistics */}
                <div className="bg-white border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">📊 Campaign Statistics</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Outbound Calls Made:</span>
                      <div className="text-gray-900 text-lg font-semibold">{stats.callsMade}</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Remaining Calls:</span>
                      <div className="text-gray-900 text-lg font-semibold">{stats.remainingCalls}</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Completion Percentage:</span>
                      <div className="text-gray-900 text-lg font-semibold">{stats.completionPercentage.toFixed(1)}%</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Phone Minutes Consumed:</span>
                      <div className="text-gray-900 text-lg font-semibold">{stats.phoneMinutes.toFixed(2)} minutes</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Validated Leads:</span>
                      <div className="text-gray-900 text-lg font-semibold">{stats.validatedLeads}</div>
                    </div>
                  </div>
                </div>

                {/* AI Configuration */}
                <div className="bg-white border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">🤖 Configuration IA</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">STT Model:</span>
                      <div className="text-gray-900">{campaignDetails.ai_config?.stt_model || 'N/A'}</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">LLM Model:</span>
                      <div className="text-gray-900">{campaignDetails.ai_config?.llm_model || 'N/A'}</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">TTS Model:</span>
                      <div className="text-gray-900">{campaignDetails.ai_config?.tts_model || 'N/A'}</div>
                    </div>
                  </div>
                </div>

                {/* SIP Configuration */}
                <div className="bg-white border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">📱 Configuration SIP</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Provider:</span>
                      <div className="text-gray-900">{campaignDetails.sip_config?.provider || 'N/A'}</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Caller ID:</span>
                      <div className="text-gray-900">{campaignDetails.sip_config?.caller_id || 'N/A'}</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Concurrent Calls:</span>
                      <div className="text-gray-900">{campaignDetails.concurrent_calls || 5}</div>
                    </div>
                  </div>
                </div>

                {/* Schedule Configuration */}
                {campaignDetails.schedule_config?.enabled && (
                  <div className="bg-white border rounded-lg p-4">
                    <h4 className="font-semibold mb-3">📅 Configuration Planning</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Timezone:</span>
                        <div className="text-gray-900">{campaignDetails.schedule_config?.timezone || 'N/A'}</div>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Days of Week:</span>
                        <div className="text-gray-900">
                          {campaignDetails.schedule_config?.days_of_week?.join(', ') || 'N/A'}
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <span className="font-medium text-gray-700">Time Slots:</span>
                        <div className="text-gray-900">
                          {campaignDetails.schedule_config?.time_slots?.map((slot, idx) => (
                            <span key={idx} className="mr-2">
                              {slot.start} - {slot.end || 'N/A'}
                            </span>
                          )) || 'N/A'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* AMD Configuration */}
                {campaignDetails.amd_config?.enabled && (
                  <div className="bg-white border rounded-lg p-4">
                    <h4 className="font-semibold mb-3">🎯 Détection de Répondeur (AMD)</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Initial Silence:</span>
                        <div className="text-gray-900">{campaignDetails.amd_config?.initial_silence || 'N/A'}ms</div>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Greeting:</span>
                        <div className="text-gray-900">{campaignDetails.amd_config?.greeting || 'N/A'}ms</div>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Total Analysis Time:</span>
                        <div className="text-gray-900">{campaignDetails.amd_config?.total_analysis_time || 'N/A'}ms</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Google Sheets Configuration */}
                {campaignDetails.google_sheet_config?.sync_enabled && (
                  <div className="bg-white border rounded-lg p-4">
                    <h4 className="font-semibold mb-3">📊 Configuration Google Sheets</h4>
                    <div className="text-sm">
                      <span className="font-medium text-gray-700">Sheet URL:</span>
                      <div className="text-gray-900 break-all">{campaignDetails.google_sheet_config?.sheet_url || 'N/A'}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignDetails;